/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import BackButton from '../common/BackButton';
import './CompanyList.css';

interface Company {
  id: number;
  name: string;
  email?: string;
  phoneNumber?: string;
  contactNotes?: string;
  generateBidCost: number;
  updateBidCost: number;
  credits: number;
}

const CompanyList: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.getAllCompanies();

      if (response.success && response.companies) {
        setCompanies(response.companies);
      } else {
        setError(response.error || 'Failed to load companies');
      }
    } catch (err: any) {
      console.error('Error fetching companies:', err);
      setError(err.message || 'An error occurred while loading companies');
    } finally {
      setLoading(false);
    }
  };

  const formatCentsAsDollars = (cents: number): string => {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(dollars);
  };

  const handleDeleteCompany = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiClient.deleteCompany(id);

      if (response.success) {
        // Refresh the list
        fetchCompanies();
      } else {
        alert(response.error || 'Failed to delete company');
      }
    } catch (err: any) {
      console.error('Error deleting company:', err);
      alert(err.message || 'An error occurred while deleting the company');
    }
  };

  if (loading) {
    return (
      <div className="company-list-container">
        <div className="loading">Loading companies...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="company-list-container">
        <BackButton to="/" />
        <div className="error-message">
          <h3>Error Loading Companies</h3>
          <p>{error}</p>
          <button onClick={fetchCompanies} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="company-list-container">
      <BackButton to="/" />

      <div className="company-list-header">
        <h2>Companies</h2>
        <button
          className="create-company-button"
          onClick={() => navigate('/companies/create')}
        >
          Create New Company
        </button>
      </div>

      {companies.length === 0 ? (
        <div className="no-companies">
          <p>No companies found.</p>
          <button
            className="create-first-button"
            onClick={() => navigate('/companies/create')}
          >
            Create Your First Company
          </button>
        </div>
      ) : (
        <div className="companies-grid">
          {companies.map((company) => (
            <div key={company.id} className="company-card">
              <div className="company-header">
                <h3>{company.name}</h3>
                <div className="company-actions">
                  <button
                    className="edit-button"
                    onClick={() => navigate(`/companies/${company.id}/edit`)}
                    title="Edit company"
                  >
                    Edit
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDeleteCompany(company.id, company.name)}
                    title="Delete company"
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="company-details">
                {company.email && (
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">{company.email}</span>
                  </div>
                )}
                {company.phoneNumber && (
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{company.phoneNumber}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Credits:</span>
                  <span className="detail-value credits">{formatCentsAsDollars(company.credits)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Generate Cost:</span>
                  <span className="detail-value">{formatCentsAsDollars(company.generateBidCost)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Update Cost:</span>
                  <span className="detail-value">{formatCentsAsDollars(company.updateBidCost)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyList;
